import _ from "lodash";
import { Renderer as MarkedRenderer, parser, lexer } from "marked";

class Renderer extends MarkedRenderer {
  // There are a few types of links in help md:
  // 1. <img> ones which should reference the directory where md file is placed
  // and it is fixed by passing baseUrl option to marked
  // 2. <a> ones which should reference relatively to the current page
  // and it is fixed by the following renderer
  link(href, title, text) {
    // All links without `#` are treated as external
    href = _.startsWith(href, "#") ? `#/help/${href.replace("#", "")}` : href;
    return super.link(href, title, text);
  }

  // According to A11Y on the page should be only one header H1
  // that's why we need to change the heading level
  heading(text, level) {
    return `<h${level + 1}> ${text}</h${level + 1}>`;
  }

  image(href, title, text) {
    const imgTag = super.image(href, "", text);

    return `<figure>${imgTag}<figcaption>${title}</figcaption></figure>`;
  }
}

export const getMarkedOptions = (language, baseUrl = "translations/help") => ({
  baseUrl: `${baseUrl}/${language}/`,
  smartypants: true,
  renderer: new Renderer(),
});

const explicitIdRegexp = /\{#(.+)\}/;

const makeId = (text, index) => {
  const explicitId = text.match(explicitIdRegexp);
  const id = !_.isEmpty(explicitId)
    ? explicitId[1]
    : // It is copied from marked's sources, since there it is used by parser and
      // we need it before parser stage
      text.toLowerCase().replace(/[^\w]+/gu, "-");

  return id !== "-" ? id : `s${index}`;
};

const splitSections = (tokens) =>
  _.transform(
    tokens,
    (sections, token, index) => {
      const section = _.last(sections);
      if (token.type === "heading" && _.includes([1, 2], token.depth)) {
        sections.push({
          id: makeId(token.text, index),
          level: token.depth,
          header: { ...token, text: token.text.replace(explicitIdRegexp, "") },
          tokens: [],
        });
      } else if (section) {
        section.tokens.push(token);
      }
    },
    []
  );

/**
 * Parses provided markdown string
 * @param mdContent string Markdown markup
 * @param options object MarkedOptions { baseUrl: string, smartypants: bool, renderer: new Renderer() }
 * @returns {Array<{id: string, level: number, header: string, content: string}>}
 */
export const parseMarkdown = (mdContent, options) => {
  const tokens = lexer(mdContent, options);

  return splitSections(tokens);
};

export const parseTokens = (tokens, options) =>
  _([...tokens]) // marked erases the tokens array
    .assign({ links: {} })
    .thru((tokens) => parser(tokens, options))
    .value();
